import React, { useEffect, useState, useRef } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import StudentLogo from '@/components/StudentLogo';
import InstitutionSwitcher from './InstitutionSwitcher';
import { examScopedPath } from '../utils/examNavigation';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();
  const exam = new URLSearchParams(location.search).get('exam') || '';
  const scopedLink = (path: string) => ['/', '/tests', '/results', '/analytics'].includes(path) ? examScopedPath(path, exam) : path;
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const greeting = user?.name || user?.username || 'Student';
  const links = [
    { to: '/', label: 'Dashboard' },
    { to: '/tests', label: 'Tests' },
    { to: '/results', label: 'Results' },
    { to: '/analytics', label: 'Analytics' },
    { to: '/announcements', label: 'Announcements' },
    { to: '/academic-plan', label: 'Calendar & timetable' },
    { to: '/attendance', label: 'Attendance' },
    { to: '/profile', label: 'Profile' },
  ];

  const previousPath = useRef(location.pathname);
  const [destination, setDestination] = useState('');
  const active = (path: string) => location.pathname === path || (path === '/results' && location.pathname.startsWith('/results/'));
  useEffect(() => {
    setMenuOpen(false);
    if (previousPath.current === location.pathname) return;
    previousPath.current = location.pathname;
    setDestination(links.find(link => active(link.to))?.label || 'Student portal');
    const frame = requestAnimationFrame(() => document.getElementById('student-main-content')?.focus());
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, isAuthenticated]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { setMenuOpen(false); menuButton.current?.focus(); }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  // Hide header and footer during test taking
  const isTestTaking = location.pathname.includes('/test/') && location.pathname.includes('/take');

  if (isTestTaking) {
    return <>{children}</>;
  }

  return (
    <div className={`student-shell min-h-screen flex flex-col bg-gray-50 ${isAuthenticated ? 'momentum-workspace' : ''}`}>
      <a href="#student-main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-white focus:px-4 focus:py-3 focus:font-semibold focus:text-foreground">Skip to content</a>
      <header className={isAuthenticated ? "momentum-navigation" : "bg-primary text-primary-foreground border-b border-primary/20 shadow-sm"}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex min-w-0 items-center justify-between gap-4">
            <Link to={scopedLink('/')} className="flex shrink-0 items-center" onClick={() => setMenuOpen(false)}>
              <StudentLogo size="md" showText={true} textColor={isAuthenticated ? "default" : "white"} />
            </Link>
            {isAuthenticated ? (
              <>
                <div className="hidden min-w-0 items-center gap-4 xl:flex">
                  <Button size="sm" onClick={logout} className="shrink-0 bg-transparent text-foreground border border-border hover:bg-accent text-xs">
                    Logout
                  </Button>
                  <span className="max-w-48 truncate text-sm text-muted-foreground" title={`Hi, ${greeting}`}>Hi, {greeting}</span>
                </div>
                <button ref={menuButton} type="button" aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
                  aria-expanded={menuOpen} aria-controls="student-mobile-navigation"
                  onClick={() => setMenuOpen(open => !open)}
                  className="flex h-11 shrink-0 items-center gap-2 rounded-md border border-border px-3 text-sm font-medium hover:bg-accent focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 xl:hidden">
                  {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
                  <span>Menu</span>
                </button>
              </>
            ) : (
              <Link to="/login" className="shrink-0">
                <Button size="sm" className="bg-white/10 text-white border border-white/20 hover:bg-white/20 text-xs">Login</Button>
              </Link>
            )}
          </div>
          {isAuthenticated && <InstitutionSwitcher />}
          {isAuthenticated && (<>
                <nav aria-label="Main navigation" className="mt-3 hidden min-w-0 flex-wrap items-center gap-2 border-t border-border pt-2 xl:flex">
                  {links.map(link => (
                    <Link key={link.to} to={scopedLink(link.to)} aria-current={active(link.to) ? 'page' : undefined}
                      className="shrink-0 rounded-md px-3 py-2 text-sm font-medium hover:bg-accent aria-[current=page]:bg-accent aria-[current=page]:font-bold aria-[current=page]:underline underline-offset-4 decoration-2 transition-colors">
                      {link.label}
                    </Link>
                  ))}
                </nav>
          </>)}
          {isAuthenticated && menuOpen && (
            <nav id="student-mobile-navigation" aria-label="Mobile navigation" className="mt-3 border-t border-border pt-3 xl:hidden">
              <p className="truncate pb-2 text-sm text-muted-foreground" title={`Hi, ${greeting}`}>Hi, {greeting}</p>
              <div className="grid grid-cols-2 gap-2">
                {links.map(link => (
                  <Link key={link.to} to={scopedLink(link.to)} onClick={() => setMenuOpen(false)}
                    aria-current={active(link.to) ? 'page' : undefined}
                    className="flex min-h-11 items-center rounded-md px-3 text-sm font-medium hover:bg-accent aria-[current=page]:bg-accent aria-[current=page]:font-bold aria-[current=page]:underline underline-offset-4 decoration-2 focus-visible:outline focus-visible:outline-2">
                    {link.label}
                  </Link>
                ))}
              </div>
              <Button onClick={() => { setMenuOpen(false); logout(); }} className="mt-3 h-11 w-full border border-border bg-secondary text-secondary-foreground hover:bg-accent">Logout</Button>
            </nav>
          )}
        </div>
      </header>
      <p role="status" aria-live="polite" className="sr-only">{destination}</p>
      <main id="student-main-content" tabIndex={-1} className="flex-grow flex flex-col min-w-0 focus:outline-none">
        {children}
      </main>
      <footer className="bg-gray-100 py-4 border-t border-gray-200">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p className="text-sm">&copy; {new Date().getFullYear()} BayesPrep Student Portal. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
